// Alta de un pedido de venta desde el archivo del cliente.
//
// Lo que se envía al final NO es un pedido: es una SOLICITUD. El flujo que lo
// crea en D365 tarda minutos y avisa por correo, así que la pantalla de cierre
// dice «solicitud cursada» y nunca «pedido creado» — la misma decisión que el
// cajón de nueva carga, y por el mismo motivo: no afirmar lo que aún no es.
//
// El cromado es el de ese cajón a propósito: en esta pantalla el usuario ya abre
// un panel lateral, y la consistencia gana a la novedad.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader } from '@/components/common/Loader'
import { RielPasos } from '@/components/common/RielPasos'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { pedidosArchivoApi } from '@/services/api'
import type {
  CabeceraBorrador,
  GrupoCliente,
  LecturaArchivo,
  LineaBorrador,
  LineaRecuperable,
  PedidoArchivoResult,
} from '@/types'
import {
  borradorDeLectura,
  construirSolicitud,
  lineaDeRecuperable,
  restablecerTodas,
  totales,
  validarCabecera,
  validarLineas,
} from '@/utils/pedidoDesdeArchivo'
import { AvisoDuplicada, type Duplicada } from './AvisoDuplicada'
import { cromado } from './cromado'
import { PasoArchivo } from './PasoArchivo'
import { PasoCabecera } from './PasoCabecera'
import { PasoConfirmar } from './PasoConfirmar'
import { PasoLineas } from './PasoLineas'

interface Props {
  dark: boolean
  empresas: string[]
  onClose: () => void
  onCursada: (resultado: PedidoArchivoResult) => void
}

interface ErrorPantalla {
  codigo: string
  valores?: Record<string, string | number>
}

const HOY = () => new Date().toISOString().slice(0, 10)

/** Saca el código de error que manda el backend, que viaja como código y no como
 *  texto porque la pantalla es bilingüe. */
function leerError(e: unknown): ErrorPantalla {
  const detalle = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detalle && typeof detalle === 'object' && 'codigo' in detalle) {
    const d = detalle as { codigo: string; valores?: Record<string, string | number> }
    return { codigo: d.codigo, valores: d.valores }
  }
  return { codigo: 'generico' }
}

export function NuevoPedidoArchivoDrawer({ dark, empresas, onClose, onCursada }: Props) {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const c = cromado(dark)

  const [open, setOpen] = useState(false)
  const [paso, setPaso] = useState(0)
  const [alcanzado, setAlcanzado] = useState(0)
  const [intentado, setIntentado] = useState(false)
  // El aviso de «esto ya lo has cursado». No es un error de pantalla: es una
  // pregunta que el usuario tiene que poder contestar que SÍ, así que vive
  // aparte de `error` —que sólo se lee— y no se pinta en el paso.
  const [duplicada, setDuplicada] = useState<Duplicada | null>(null)
  const [error, setError] = useState<ErrorPantalla | null>(null)
  const [cursada, setCursada] = useState<PedidoArchivoResult | null>(null)

  const [archivos, setArchivos] = useState<File[]>([])
  const [lectura, setLectura] = useState<LecturaArchivo | null>(null)
  const [lineas, setLineas] = useState<LineaBorrador[]>([])
  const [recuperables, setRecuperables] = useState<LineaRecuperable[]>([])

  const [cab, setCab] = useState<CabeceraBorrador>({
    empresa: empresas.length === 1 ? empresas[0] : '',
    grupo: '' as GrupoCliente,
    clienteId: '', clienteNombre: '', numeroPedidoCliente: '', moneda: '',
    tipoPago: '',
    direccionId: '', direccionTexto: '',
    fecha: HOY(), almacen: '', nota: '', copia: '',
  })

  const almacenes = useQuery({
    queryKey: ['almacenes-venta'],
    queryFn: () => pedidosArchivoApi.almacenes(),
    staleTime: 60 * 60 * 1000,   // es una constante del backend, no un dato vivo
  })

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [])

  function handleClose() {
    setOpen(false)
    setTimeout(onClose, 340)
  }

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Lectura ────────────────────────────────────────────────────────────────

  const leer = useMutation({
    mutationFn: () => pedidosArchivoApi.leer({
      archivos,
      empresa: cab.empresa,
      grupo: cab.grupo as GrupoCliente,
    }),
    onSuccess: (datos) => {
      setLectura(datos)
      setLineas(borradorDeLectura(datos))
      setRecuperables(datos.recuperables)
      setCab((v) => ({
        ...v,
        // Lo que el flujo reconoció entra como PROPUESTA: el cliente se puede
        // cambiar en el paso siguiente, porque equivocarse de cliente y no poder
        // corregirlo obligaría a volver a empezar con otro archivo.
        clienteId: datos.clienteId,
        clienteNombre: datos.clienteNombre,
        numeroPedidoCliente: datos.numeroPedidoCliente,
        moneda: datos.moneda,
        tipoPago: datos.tipoPago,
      }))
      setError(null)
      setIntentado(false)
      setPaso(1)
      setAlcanzado((a) => Math.max(a, 1))
    },
    onError: (e) => setError(leerError(e)),
  })

  // ── Envío ──────────────────────────────────────────────────────────────────

  const cursar = useMutation({
    mutationFn: (confirmarDuplicado: boolean = false) => pedidosArchivoApi.crear(
      construirSolicitud(
        cab,
        lineas,
        // Lo que diga la lectura, que es lo que el flujo confirmó haber abierto;
        // si no lo dijera, lo que subimos.
        lectura?.archivos?.length
          ? lectura.archivos
          : archivos.map((f) => ({ nombre: f.name, bytes: f.size })),
        confirmarDuplicado,
      ),
    ),
    onSuccess: (resultado) => {
      setDuplicada(null)
      setCursada(resultado)
      onCursada(resultado)
    },
    onError: (e) => {
      const fallo = leerError(e)
      // El duplicado NO es un error: es una pregunta. Pintarlo en la franja
      // roja del paso lo dejaba como un muro, que es justo lo que no es.
      if (fallo.codigo === 'duplicada') {
        setDuplicada((fallo.valores ?? {}) as Duplicada)
        return
      }
      setError(fallo)
    },
  })

  // ── Pasos ──────────────────────────────────────────────────────────────────

  const faltanCab = validarCabecera(cab)
  const faltanLin = validarLineas(lineas)
  const total = useMemo(() => totales(lineas), [lineas])

  const pasos = [
    {
      id: 'archivo',
      titulo: t('salesOrders.desdeArchivo.pasos.archivo'),
      resumen: lectura
        ? lectura.archivos.map((a) => a.nombre).join(' · ')
        : undefined,
    },
    {
      id: 'cabecera',
      titulo: t('salesOrders.desdeArchivo.pasos.cabecera'),
      resumen: cab.clienteId ? (cab.clienteNombre || cab.clienteId) : undefined,
    },
    {
      id: 'lineas',
      titulo: t('salesOrders.desdeArchivo.pasos.lineas'),
      resumen: alcanzado >= 2
        ? t('salesOrders.desdeArchivo.resumenLineas', {
            lineas: total.lineas, unidades: total.unidades.toLocaleString(),
          })
        : undefined,
    },
    { id: 'confirmar', titulo: t('salesOrders.desdeArchivo.pasos.confirmar') },
  ]

  /** Hacia adelante sólo con el paso resuelto: dejar saltar al final con la
   *  cabecera a medias produce un error que el usuario no sabe de dónde viene. */
  function siguiente() {
    setIntentado(true)
    if (paso === 1 && faltanCab.length > 0) return
    if (paso === 2 && faltanLin.length > 0) return
    setError(null)
    setIntentado(false)
    const destino = Math.min(paso + 1, pasos.length - 1)
    setPaso(destino)
    setAlcanzado((a) => Math.max(a, destino))
  }

  const puedeCursar = faltanCab.length === 0 && faltanLin.length === 0 && !cursar.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div
        onClick={handleClose}
        className="absolute inset-0 cursor-pointer"
        style={{
          backgroundColor: open ? 'rgba(0,0,0,0.52)' : 'rgba(0,0,0,0)',
          backdropFilter: open ? 'blur(3px)' : 'blur(0px)',
          transition: 'background-color 0.34s ease, backdrop-filter 0.34s ease',
        }}
      />

      <div
        className={`relative flex flex-col h-full shadow-2xl ${c.bg} border-l ${c.border}`}
        style={{
          width: isMobile ? '100vw' : 'min(94vw, 980px)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.34s cubic-bezier(0.32, 0, 0.1, 1)',
          boxShadow: dark
            ? '-32px 0 80px rgba(0,0,0,0.6), -1px 0 0 rgba(255,255,255,0.05)'
            : '-32px 0 80px rgba(0,0,0,0.12), -1px 0 0 rgba(0,0,0,0.06)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('salesOrders.desdeArchivo.titulo')}
      >
        {/* ── Cabecera ─────────────────────────────────────────────────── */}
        <div className={`flex items-center gap-4 px-6 py-4 border-b ${c.border} shrink-0`}>
          <div className={`p-2 rounded-lg ${dark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <svg className={`w-4 h-4 ${c.mutedTxt}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <path d="M14 2v6h6M9 15l2 2 4-4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-[13px] font-semibold ${c.mainTxt}`}>
              {t('salesOrders.desdeArchivo.titulo')}
            </h2>
            <p className={`text-[11px] mt-0.5 flex items-center gap-2 flex-wrap ${c.mutedTxt}`}>
              {cab.empresa && <span className="font-mono">{cab.empresa.toUpperCase()}</span>}
              {cab.empresa && cab.grupo && <span className={c.dimTxt}>·</span>}
              {cab.grupo && <span>{t(`salesOrders.desdeArchivo.grupos.${cab.grupo}`)}</span>}
              {!cab.empresa && !cab.grupo && t('salesOrders.desdeArchivo.subtitulo')}
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label={t('salesOrders.nuevaCarga.cerrar')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors cursor-pointer ${
              dark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6'
                   : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            <span className={`text-[10px] font-mono hidden sm:inline ${c.dimTxt}`}>ESC</span>
          </button>
        </div>

        {cursada ? (
          <Confirmacion dark={dark} resultado={cursada} onClose={handleClose} />
        ) : (
          <>
            <RielPasos
              dark={dark}
              pasos={pasos}
              actual={paso}
              alcanzado={alcanzado}
              onIr={(i) => { setError(null); setIntentado(false); setPaso(i) }}
            />

            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
              {paso === 0 && (
                <PasoArchivo
                  dark={dark}
                  empresas={empresas}
                  empresa={cab.empresa}
                  grupo={cab.grupo}
                  archivos={archivos}
                  leyendo={leer.isPending}
                  error={error}
                  onEmpresa={(v) => { setError(null); setCab((s) => ({ ...s, empresa: v })) }}
                  onGrupo={(v) => { setError(null); setCab((s) => ({ ...s, grupo: v })) }}
                  onArchivos={(f) => { setError(null); setArchivos(f) }}
                  onError={(codigo, valores) => setError({ codigo, valores })}
                  onLeer={() => leer.mutate()}
                />
              )}

              {paso === 1 && (
                <PasoCabecera
                  dark={dark}
                  cab={cab}
                  almacenes={almacenes.data ?? []}
                  intentado={intentado}
                  faltan={faltanCab}
                  onCambio={(patch) => { setError(null); setCab((s) => ({ ...s, ...patch })) }}
                />
              )}

              {paso === 2 && (
                <PasoLineas
                  dark={dark}
                  cab={cab}
                  lineas={lineas}
                  recuperables={recuperables}
                  almacenes={almacenes.data ?? []}
                  onLinea={(id, patch) =>
                    setLineas((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)))}
                  onQuitar={(id) => setLineas((ls) => ls.filter((l) => l.id !== id))}
                  onRestablecer={(campo) => setLineas((ls) => restablecerTodas(ls, campo))}
                  onRecuperar={(r, i) => {
                    setLineas((ls) => [...ls, lineaDeRecuperable(r)])
                    // Se quita de la lista de ofrecidas: dejarla invitaría a
                    // añadirla dos veces sin darse cuenta.
                    setRecuperables((rs) => rs.filter((_, j) => j !== i))
                  }}
                />
              )}

              {paso === 3 && (
                <PasoConfirmar
                  dark={dark}
                  cab={cab}
                  lineas={lineas}
                  archivos={lectura?.archivos ?? []}
                  error={error}
                />
              )}
            </div>

            {/* ── Pie ──────────────────────────────────────────────────── */}
            <div className={`flex items-center gap-3 flex-wrap px-6 py-3.5 border-t ${c.border} shrink-0`}>
              <p className={`text-[11px] flex-1 min-w-0 ${c.mutedTxt}`}>
                {lineas.length > 0 && (
                  <>
                    <span className={`font-semibold ${c.mainTxt}`}>
                      {t('salesOrders.desdeArchivo.lineasN', { count: total.lineas })}
                    </span>
                    <span className={c.dimTxt}> · </span>
                    <span className="tabular-nums font-semibold">
                      {total.unidades.toLocaleString()}
                    </span>{' '}
                    {t('salesOrders.desdeArchivo.unidades').toLowerCase()}
                  </>
                )}
              </p>

              {cursar.isPending && <Loader size={7} />}

              {paso > 0 && (
                <button
                  type="button"
                  onClick={() => { setError(null); setIntentado(false); setPaso((p) => p - 1) }}
                  className={`px-3 py-2 rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${
                    dark ? 'text-slate-300 hover:bg-white/6' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {t('salesOrders.desdeArchivo.atras')}
                </button>
              )}

              {paso < 3 ? (
                <button
                  type="button"
                  disabled={paso === 0 && !lectura}
                  onClick={siguiente}
                  className={`px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors shadow-sm ${
                    paso === 0 && !lectura
                      ? `cursor-not-allowed ${dark ? 'bg-white/8 text-slate-500' : 'bg-slate-200 text-slate-400'}`
                      : 'bg-sky-600 text-white hover:bg-sky-500 cursor-pointer'
                  }`}
                >
                  {t('salesOrders.desdeArchivo.siguiente')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setIntentado(true); if (puedeCursar) cursar.mutate(false) }}
                  disabled={cursar.isPending}
                  title={puedeCursar ? undefined : t('salesOrders.desdeArchivo.faltaAlgo')}
                  className={`px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors shadow-sm ${
                    puedeCursar
                      ? 'bg-sky-600 text-white hover:bg-sky-500 cursor-pointer'
                      : `cursor-not-allowed ${dark ? 'bg-white/8 text-slate-500' : 'bg-slate-200 text-slate-400'}`
                  }`}
                >
                  {cursar.isPending
                    ? t('salesOrders.desdeArchivo.cursando')
                    : t('salesOrders.desdeArchivo.cursar')}
                </button>
              )}
            </div>

            {duplicada && (
              <AvisoDuplicada
                dark={dark}
                datos={duplicada}
                enviando={cursar.isPending}
                onCancelar={() => setDuplicada(null)}
                onEnviar={() => cursar.mutate(true)}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Confirmación ──────────────────────────────────────────────────────────────

/** Sustituye al formulario entero, como en el cajón de nueva carga.
 *
 *  NO dice «pedido creado»: lo crea un flujo que tarda minutos. Decirlo aquí
 *  mandaría al usuario a buscar en la lista un pedido que todavía no existe. */
function Confirmacion({ dark, resultado, onClose }: {
  dark: boolean; resultado: { solicitudId: string }; onClose: () => void
}) {
  const { t } = useTranslation()
  const c = cromado(dark)

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8 py-12 text-center">
      <div className={`w-14 h-14 rounded-2xl grid place-items-center ${
        dark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
      }`}>
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>

      <div className="space-y-2 max-w-md">
        <h3 className={`text-[15px] font-semibold ${c.mainTxt}`}>
          {t('salesOrders.desdeArchivo.cursada.titulo')}
        </h3>
        <p className={`text-[12px] leading-relaxed ${c.mutedTxt}`}>
          {t('salesOrders.desdeArchivo.cursada.texto')}
        </p>
      </div>

      <div className={`px-3.5 py-2 rounded-lg border ${c.panel} ${c.panelBorde}`}>
        <span className={`text-[10px] uppercase tracking-wide ${c.dimTxt}`}>
          {t('salesOrders.desdeArchivo.cursada.referencia')}
        </span>
        <p className={`text-[11px] font-mono mt-0.5 ${c.mainTxt}`}>{resultado.solicitudId}</p>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-lg text-[12px] font-semibold bg-sky-600 text-white hover:bg-sky-500 cursor-pointer transition-colors"
      >
        {t('salesOrders.desdeArchivo.cursada.cerrar')}
      </button>
    </div>
  )
}
